import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';

import { useAppBusy, useAppVersion } from '../lib/appVersion';

/**
 * "A new version is ready" for a terminal that never gets closed.
 *
 * It reloads ON ITS OWN only while nothing is busy, because a POS must not
 * reload mid-sale: a cart with items on it is unsaved work belonging to a
 * customer standing at the counter. With a cart open the banner simply waits,
 * and the cashier can take it at the moment that suits them.
 *
 * GRACE_MS exists so a reload never lands in the same second the banner
 * appears, which from behind the counter looks like the machine crashing.
 */
const GRACE_MS = 20 * 1000;

export default function UpdateBanner() {
  const { updateAvailable, deployedVersion, builtVersion, reload } = useAppVersion();
  const isBusy = useAppBusy();
  const [seenAt, setSeenAt] = useState(null);

  useEffect(() => {
    if (updateAvailable && seenAt === null) setSeenAt(Date.now());
    if (!updateAvailable && seenAt !== null) setSeenAt(null);
  }, [updateAvailable, seenAt]);

  useEffect(() => {
    if (!updateAvailable || isBusy || seenAt === null) return undefined;
    const wait = Math.max(0, GRACE_MS - (Date.now() - seenAt));
    const id = setTimeout(() => {
      // Re-check at the moment of firing: a cashier may have started an order
      // during the grace period.
      if (!isBusy) reload();
    }, wait);
    return () => clearTimeout(id);
  }, [updateAvailable, isBusy, seenAt, reload]);

  if (!updateAvailable) return null;

  return (
    <div
      role="status"
      data-testid="update-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3
                 rounded-full border border-border bg-card px-4 py-2 shadow-lg"
    >
      <RefreshCw className="h-4 w-4 text-secondary" aria-hidden="true" />
      <span className="text-sm text-foreground">
        {isBusy
          ? 'A new version is ready. It will load once this order is finished.'
          : 'A new version is ready.'}
      </span>
      <button
        type="button"
        onClick={reload}
        data-testid="update-banner-reload"
        className="rounded-full bg-secondary px-3 py-1 text-sm font-semibold text-white
                   hover:bg-secondary/90"
      >
        Reload now
      </button>
      <span className="sr-only">
        Running {builtVersion}, available {deployedVersion}
      </span>
    </div>
  );
}
